import React, { createContext, useEffect, useState } from 'react'
import { onError } from '@apollo/client/link/error'
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  ApolloProvider,
  ApolloLink
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { has } from 'lodash'
import { useSelector } from 'react-redux'
import { showNotification } from './actions/notification.action'
import store from './store'
import { hideLoader, showLoader } from './actions/loader.action'
import { kibanaLog } from './helpers/loggerUtil'

export let apolloClient = null

export const setApolloClient = client => (apolloClient = client)

const loaderLink = new ApolloLink((operation, forward) => {
  store.dispatch(showLoader())
  return forward(operation).map(response => {
    store.dispatch(hideLoader())
    return response
  })
})

// Error handling link for GraphQL errors
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  kibanaLog({
    message: networkError ? 'Network Error' : 'Graphql Api getting failed',
    stack: {
      error: graphQLErrors || networkError,
      operation
    }
  })
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      // Show error notification
      if (extensions?.response?.body?.error_description) {
        store.dispatch(
          showNotification({
            class: 'is-danger',
            message: extensions.response.body.error_description
          })
        )
      } else if (
        extensions?.response?.body?.errors &&
        extensions?.response?.body?.errors?.length > 0
      ) {
        extensions?.response?.body?.errors.forEach(({ detail }) => {
          store.dispatch(
            showNotification({
              class: 'is-danger',
              message: detail
            })
          )
          return
        })
        return
      } else if (extensions?.response?.body?.message) {
        store.dispatch(
          showNotification({
            class: 'is-danger',
            message: extensions.response.body.message
          })
        )
      } else if (message) {
        store.dispatch(
          showNotification({
            class: 'is-danger',
            message
          })
        )
      } else {
        store.dispatch(
          showNotification({
            class: 'is-danger',
            message: 'Network Error'
          })
        )
      }
    })
  }

  if (networkError) {
    showNotification('Network Error', 'error', 2000)
  }
})

export const createApolloClient = (graphqlUrl, token) => {
  const httpLink = new HttpLink({
    uri: graphqlUrl
  })
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : ''
    }
  }))

  return new ApolloClient({
    link: from([loaderLink, errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        PersonMovement: {
          keyFields: false
        },
        PersonLocationMovement: {
          keyFields: false
        }
      }
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network'
      }
    }
  })
}

export const ApolloContext = createContext({ isClientWithUrlSet: false })

const ApolloClientProvider = ({ children }) => {
  const urls = useSelector(state => state?.startup)
  const token = useSelector(state => state?.auth?.tokenDetail?.accessToken)
  const [client, setClient] = useState(() => {
    const initialClient = createApolloClient('', token)
    setApolloClient(initialClient)
    return initialClient
  })
  const [isGqlUrlSet, setIsGqlUrlSet] = useState(has(urls, 'graphql'))

  useEffect(() => {
    if (has(urls, 'graphql')) {
      const apolloClient = createApolloClient(urls.graphql, token)
      setIsGqlUrlSet(true)
      setClient(apolloClient)
      setApolloClient(apolloClient)
    }
  }, [urls, token])

  if (!client) return children

  return (
    <ApolloContext.Provider value={{ isClientWithUrlSet: isGqlUrlSet }}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </ApolloContext.Provider>
  )
}

export default ApolloClientProvider
